<!--
  StoreDefaultsSection -- per-store defaults auto-inherited by new products.

  Phase 4.11 (route fork) -- 2026-06-13 design bundle
  Card: https://trello.com/c/hYlwyKYT
  Plan: planning/design-handoffs/handoff_2026-06-13/implementation-plan/05-reuse-scale.md
        (build-order step 20)

  One knob:

    Default decoration set -- DecorationSetPicker (P4.8). The set's
    placements snapshot onto each new product. Note: the snapshot
    helper that forward-applies a set to a product ships with Phase
    4.7's apply endpoint; until then the column is captured here
    for future auto-apply but is NOT applied on create. The UI
    still surfaces the choice so the wire is ready.

  "Default margin %" was removed on 2026-09-29 (tf7-super, for Jake's
  no-confusion requirement). It wrote stores.default_margin_percent, which
  only ever fed campaign_products.default_margin -- no longer a price input
  under two-mode pricing (Fixed price | Store pricing). The store Pricing
  grid is the single place a store-wide margin lives. Saves omit the key,
  and the backend PATCH is partial, so an existing stored value is left
  untouched.

  A third knob, "Default colors", was removed on 2026-08-13 (card
  https://trello.com/c/jHm00hqU). It was write-only: the backend
  snapshotted the hex list onto each new product under an
  ``available_colors`` attributes key for a downstream consumer that
  was never built, while the product editor's real color source is
  Artifi/SanMar coverage keyed on the supplier SKU (ColorsPanel.vue).
  A control that silently does nothing is worse than an absent one.

  Persistence: own GET + PATCH against /api/stores/<id>/defaults/. The
  parent store-editor save flow does NOT include this section -- the
  endpoint is its own surface so saves here don't intermix with the
  store metadata save.

  Test-id prefix: ct-defaults-*
-->
<template>
    <editor-card
        title="Defaults"
        description="New products auto-inherit these on create."
        data-testid="ct-defaults-root"
    >
        <div v-if="loading" class="defaults__loading" data-testid="ct-defaults-loading">
            Loading defaults...
        </div>

        <div v-else class="defaults__form">
            <!-- Default decoration set -->
            <div class="defaults__row">
                <label class="defaults__label" for="ct-defaults-set">
                    Default decoration set
                </label>
                <div class="defaults__field">
                    <decoration-set-picker
                        v-model="form.default_decoration_set_id"
                        placeholder="Filter sets by name or client..."
                        data-testid="ct-defaults-set"
                        @update:modelValue="markDirty"
                    />
                    <button
                        v-if="form.default_decoration_set_id != null"
                        type="button"
                        class="defaults__clear-btn"
                        data-testid="ct-defaults-set-clear"
                        @click="clearSet"
                    >
                        Clear
                    </button>
                    <p
                        class="defaults__help"
                        data-testid="ct-defaults-set-help"
                    >
                        New products in this store start with these
                        decorations pre-applied.
                    </p>
                </div>
            </div>

            <!-- Save row -->
            <div class="defaults__save-row">
                <button
                    type="button"
                    class="defaults__save-btn"
                    :disabled="!canSave"
                    data-testid="ct-defaults-save"
                    @click="save"
                >
                    {{ saving ? 'Saving...' : 'Save defaults' }}
                </button>
                <p
                    v-if="saveError"
                    class="defaults__error"
                    data-testid="ct-defaults-save-error"
                >
                    {{ saveError }}
                </p>
                <p
                    v-if="savedAt"
                    class="defaults__hint"
                    data-testid="ct-defaults-saved"
                >
                    Saved.
                </p>
            </div>
        </div>
    </editor-card>
</template>

<script>
import axios from "@/axios";
import EditorCard from "./components/EditorCard.vue";
import DecorationSetPicker from "@/pages/distributor/decoration-sets/DecorationSetPicker.vue";

export default {
    name: "StoreDefaultsSection",

    components: {
        EditorCard,
        DecorationSetPicker,
    },

    props: {
        // Store id from the parent editor. Used to build the GET / PATCH
        // URL. Required -- the section makes no sense without a store id
        // (this section is only mounted in EDIT mode, never CREATE).
        storeId: {
            type: [Number, String],
            required: true,
        },
    },

    emits: ["defaults-changed"],

    data() {
        return {
            loading: true,
            saving: false,
            saveError: "",
            dirty: false,
            savedAt: null,
            form: {
                default_decoration_set_id: null,
            },
        };
    },

    computed: {
        canSave() {
            return this.dirty && !this.saving && !this.loading;
        },
    },

    async mounted() {
        await this.fetchDefaults();
    },

    methods: {
        markDirty() {
            this.dirty = true;
            this.savedAt = null;
        },

        async fetchDefaults() {
            this.loading = true;
            this.saveError = "";
            try {
                const resp = await axios.get(
                    `/api/stores/${this.storeId}/defaults/`,
                );
                const data = resp?.data || {};
                this.form.default_decoration_set_id =
                    data.default_decoration_set_id ?? null;
                this.dirty = false;
            } catch (e) {
                this.saveError =
                    "Could not load store defaults. Please refresh.";
            } finally {
                this.loading = false;
            }
        },

        clearSet() {
            this.form.default_decoration_set_id = null;
            this.markDirty();
        },

        async save() {
            if (!this.canSave) return;
            this.saving = true;
            this.saveError = "";
            this.savedAt = null;
            try {
                // default_margin_percent is deliberately NOT sent (removed
                // 2026-09-29); the PATCH is partial, so a stored value is
                // left as-is.
                const payload = {
                    default_decoration_set_id:
                        this.form.default_decoration_set_id,
                };
                const resp = await axios.patch(
                    `/api/stores/${this.storeId}/defaults/`,
                    payload,
                );
                const data = resp?.data || {};
                // Re-hydrate from the server response so the canonical
                // shape wins.
                this.form.default_decoration_set_id =
                    data.default_decoration_set_id ?? null;
                this.dirty = false;
                this.savedAt = Date.now();
                this.$emit("defaults-changed", { ...data });
            } catch (e) {
                const msg = e?.response?.data?.message || "Save failed.";
                this.saveError = msg;
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.defaults__loading {
    color: var(--tf-text-muted, #4b5563);
    padding: 12px 0;
}

.defaults__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.defaults__row {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.defaults__label {
    font-weight: 600;
    color: var(--tf-text, #1f2937);
    font-size: 0.9rem;
}

.defaults__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.defaults__clear-btn,
.defaults__save-btn {
    background: transparent;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 0.85rem;
    cursor: pointer;
    color: var(--tf-text, #1f2937);
}

.defaults__save-btn {
    background: var(--tf-primary, #2563eb);
    color: #fff;
    border-color: var(--tf-primary, #2563eb);
}

.defaults__save-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.defaults__error {
    color: var(--tf-error, #b91c1c);
    font-size: 0.85rem;
    margin: 0;
}

.defaults__hint {
    color: var(--tf-text-muted, #4b5563);
    font-size: 0.85rem;
    margin: 0;
}

.defaults__help {
    color: var(--tf-fg-3, #616161);
    font-size: 0.875rem;
    margin: 4px 0 0;
    line-height: 1.4;
}

.defaults__save-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
}
</style>
